import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { fila } from '../components/movimiento';
import { api, PAPELES } from '../api';
import { useSesion } from '../lib/sesion';
import { useApi, VIVO } from '../lib/useApi';
import {
  Buscador,
  Cargando,
  EstadoChip,
  filtrar,
  Paginador,
  PanelError,
  usePaginado,
  Vacio,
} from '../components/ui';
import { Dato } from '../components/EdicionContenedor';
import { botonPrimario, c, enlace, subtitulo, tarjeta } from '../theme';

/**
 * La lista de contenedores: la única. Cada renglón abre su ficha, que es donde
 * se corrigen la factura, el documento de recepción, el lote, la fecha y el
 * muelle (y se borra): editar acá y allá eran dos caminos para lo mismo.
 */
export default function DatosContenedores({
  onVerFicha,
  onNuevo,
}: {
  onVerFicha: (id: string) => void;
  onNuevo: () => void;
}) {
  const puedeCargar = useSesion().vistas.includes('contenedores');
  const lista = useApi(() => api.contenedores(), [], VIVO.normal);
  const [busqueda, setBusqueda] = useState('');

  // Por número de llegada, el más nuevo arriba: es el que casi siempre falta completar.
  const ordenados = [...(lista.datos ?? [])].sort((a, b) => (b.numero ?? 0) - (a.numero ?? 0));
  const encontrados = filtrar(ordenados, busqueda, (ct) => [
    ct.id,
    ct.factura,
    ct.docRecepcion,
    ct.lote,
    ct.estado,
    ct.muelle,
  ]);
  const pagina = usePaginado(encontrados, 15);

  if (lista.error) return <PanelError mensaje={lista.error} onReintentar={lista.recargar} />;
  if (lista.cargando && !lista.datos) return <Cargando texto="Buscando contenedores…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ ...subtitulo, marginTop: 0, flex: 1, minWidth: 'min(260px, 100%)' }}>
          Todos los contenedores. Tocá uno para ver su ficha, corregir sus datos (lo que cargues sale precargado en las
          etiquetas de capacho de lo que bajó de él) o borrarlo.
        </div>
        {puedeCargar && (
          <button type="button" onClick={onNuevo} style={botonPrimario}>
            Nuevo contenedor
          </button>
        )}
      </div>

      <div className="flex"><Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Contenedor, factura, documento o estado" /></div>

      {encontrados.length === 0 ? (
        <div style={tarjeta}>
          <Vacio icono="description" titulo="Sin contenedores" detalle="Se cargan en Nuevo contenedor." />
        </div>
      ) : (
        <div style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
          <AnimatePresence mode="popLayout" initial={false}>
          {pagina.visibles.map((ct, i) => (
            <motion.div
              key={ct.id}
              layout="position"
              {...fila(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                borderBottom: `1px solid ${c.borde}`,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() => onVerFicha(ct.id)}
                title="Ver la ficha del contenedor"
                style={{ ...enlace, fontSize: 13.5, fontWeight: 600, minWidth: 130 }}
              >
                {ct.numero ? <span style={{ color: c.texto3, fontWeight: 500 }}>Nº {ct.numero} · </span> : null}
                {ct.id}
              </button>
              <span style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: c.texto2 }}>
                {ct.fecha}
                {PAPELES.map((p) => (
                  <span key={p.campo}>
                    {' · '}
                    {p.corto} <Dato valor={ct[p.campo]} />
                  </span>
                ))}
                {ct.muelle ? ` · muelle ${ct.muelle}` : ''}
              </span>
              <EstadoChip estado={ct.estado} />
            </motion.div>
          ))}
          </AnimatePresence>
          <Paginador
            pagina={pagina.pagina}
            paginas={pagina.paginas}
            total={pagina.total}
            desde={pagina.desde}
            porPagina={15}
            que="contenedores"
            onIr={pagina.setPagina}
          />
        </div>
      )}
    </div>
  );
}
